import moment from 'moment'
import { GetList } from '../types/EmployeeDirectory/EmployeesList/AddNewEmployee/addNewEmployeeType'
import {
  MappedTabs,
  MyProfileTabList,
} from '../types/MyProfile/ProfileLandingPage/myProfileTabsTypes'
import { UserAccessToFeatures } from '../types/Settings/UserRolesConfiguration/userAccessToFeaturesTypes'
import { GetAllJobVacanciesList } from '../types/Recruitment/JobOpenings/jobOpeningsTypes'
import { Label } from '../types/commonTypes'
import { emptyString } from '../constant/constantData'

//---------------------------//
// CONSTANTS
//---------------------------//

export const TextDanger = 'text-danger'
export const TextWhite = 'text-white'

export const selectDateOptions = [
  { label: 'Today', name: 'Today' },
  { label: 'Yesterday', name: 'Yesterday' },
  { label: 'This Week', name: 'This Week' },
  { label: 'Last Week', name: 'Last Week' },
  { label: 'Last Month', name: 'Last Month' },
  { label: 'Current Month', name: 'Current Month' },
  { label: 'Custom', name: 'Custom' },
]

//---------------------------//
// FUNCTIONS
//---------------------------//

/**
 * Checks if a given string is empty.
 *
 * This function evaluates whether the provided string is `undefined`, `null`,
 * an empty string, or the string literals 'null' or 'undefined'.
 *
 * @param {string | undefined | null} str - The string to check.
 * @returns {boolean} `true` if the string is empty, otherwise `false`.
 */
export const StringEmpty = (str: string | undefined | null): boolean => {
  // All conditions are necessary to check for empty string for all cases
  return (
    str === undefined ||
    str === null ||
    str === emptyString ||
    str === 'null' ||
    str === 'undefined'
  )
}

/**
 * Compares two strings and returns a number indicating their relative order.
 *
 * @param str1 - The first string to compare. It can be a string, undefined, or null.
 * @param str2 - The second string to compare. It can be a string, undefined, or null.
 * @returns A number indicating the relative order of the strings:
 * - A negative number if `str1` comes before `str2`.
 * - Zero if `str1` and `str2` are considered equal.
 * - A positive number if `str1` comes after `str2`.
 *
 * If either of the strings is undefined or null, or if an error occurs during comparison,
 * the function logs an error message and returns 0.
 */
export const StringCompare = (
  str1: string | undefined | null,
  str2: string | undefined | null,
): number => {
  let str1Value = str1
  let str2Value = str2
  if (StringEmpty(str1)) str1Value = emptyString
  if (StringEmpty(str2)) str2Value = emptyString
  try {
    return (str1Value as string).localeCompare(str2Value as string)
  } catch (error) {
    console.error('Error occurred while comparing strings:', error)
    return 0
  }
}

/**
 * Compares the string values of a specified property between two objects.
 * @template T - The type of the objects being compared.
 * @param a - The first object to compare.
 * @param b - The second object to compare.
 * @param propertyName - The name of the property to compare.
 * @returns A number indicating the result of the comparison:
 *          - A negative number if the property value of `a` is less than the property value of `b`.
 *          - Zero if the property values are equal.
 *          - A positive number if the property value of `a` is greater than the property value of `b`.
 * @throws Will log an error to the console and return 0 if an error occurs during comparison.
 */
export function compareStringProperty<T>(
  a: T,
  b: T,
  propertyName: string,
): number {
  try {
    const nameA = getProperty(a, propertyName as keyof T) ?? '' // Use empty string if name is null
    const nameB = getProperty(b, propertyName as keyof T) ?? '' // Use empty string if name is null

    return StringCompare(String(nameA), String(nameB))
  } catch (error) {
    console.error('Error occurred while comparing string property:', error)
    return 0
  }
}

/**
 * Returns a specified string if the input string is empty, undefined, or null.
 *
 * @param str - The input string which can be of type string, undefined, or null.
 * @param trueResult - The string to return if the input string is empty, undefined, or null.
 * @returns The input string if it is not empty, otherwise returns the trueResult string.
 */
export function conditionalDefaultStringEmpty(
  str: string | undefined | null,
  trueResult: string,
): string {
  return StringEmpty(str) ? trueResult : (str as string)
}

export const listComposer = (
  list: never[],
  id: string,
  name: string,
): GetList[] => {
  if (list == null || list.length === 0 || !Array.isArray(list)) return []

  return list.map(
    (val) =>
      ({
        id: val[id],
        name: val[name],
      } as GetList),
  )
}
export const listStatus = (list: never[], name: string): GetList[] => {
  if (list == null || list.length === 0 || !Array.isArray(list)) return []

  return list.map(
    (val) =>
      ({
        name: val[name],
      } as unknown as GetList),
  )
}
export const hasLowerCase = (str: string): boolean => {
  return str?.toUpperCase() !== str
}

export const numberWithCommasFormat = (number: number | string): string => {
  if (isNaN(Number(number))) {
    return String(number)
  }
  return Number(number)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, '$&,')
    .replace('$', '')
    .replace('.00', '')
}

export const doesNotAllowAlphaNumericInput = (value: string): string =>
  value == null ||
  value === '' ||
  !isNaN(Number(value)) ||
  !/^[a-zA-Z]+$/.test(value)
    ? TextDanger
    : TextWhite

export const doesNotAllowEmptyString = (value: string): string => {
  return value === '' || /^\d+$/.test(value) ? TextDanger : TextWhite
}

/**
 * @NotRecommended - This function is not recommended to use as it is only applicable to the specific use case for input validation.
 *
 * @Suggestion - Use the `booleanMandatory()` function with `StringEmpty()` instead.
 *
 * booleanMandatory(StringEmpty(value))
 */
export const showIsRequired = (value: string): string =>
  value == null || value === '' ? TextDanger : TextWhite

export const booleanMandatory = (value: boolean): string =>
  value ? TextDanger : TextWhite

export const downloadFile = (
  excelDownload: Blob | undefined,
  downloadFormat: string,
): void => {
  if (excelDownload) {
    //removed the type object from the blob as the file extension is already provided in the downloadFormat
    const url = window.URL.createObjectURL(new Blob([excelDownload]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', downloadFormat)
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
}
export const currentMonthDate = moment().subtract(1, 'months').format('M/YYYY')
export const previousMonthDate = moment().subtract(2, 'months').format('M/YYYY')
// TODO: Relocate this function to a more appropriate or related file (e.g. dateUtils)
export const currentMonthDateNew = (country?: string): string => {
  const curentCountry = country ?? ''

  let monthNumber = new Date().getMonth()
  let year = new Date().getFullYear()
  const day = new Date().getDate()

  if (curentCountry.toLocaleUpperCase() === 'INDIA' && day >= 25 && day <= 31) {
    monthNumber = new Date().getMonth() + 1
    if (monthNumber === 12) {
      monthNumber = 0
      year = new Date().getFullYear() + 1
    }
  }
  return `${monthNumber}/${year}`
}
// TODO: Relocate this function to a more appropriate or related file (e.g. dateUtils)
export const previousMonthNew = (country?: string): string => {
  const curentCountry = country ?? ''

  let monthNumber = new Date().getMonth() - 1
  let year = new Date().getFullYear()
  const day = new Date().getDate()

  if (monthNumber === -1) {
    monthNumber = 11
    year = new Date().getFullYear() - 1
  }
  if (curentCountry.toLocaleUpperCase() === 'INDIA' && day >= 25 && day <= 31) {
    monthNumber = new Date().getMonth()
    year = new Date().getFullYear()
  }
  return `${monthNumber}/${year}`
}
const matcher =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

export const isEmail = (value: string): boolean => !matcher.test(value)

export const deviceLocale: string =
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language

export const mapTabsToFeatures = (
  TabsLabels: MyProfileTabList[],
  filteredTabs: UserAccessToFeatures[],
): MappedTabs[] => {
  return TabsLabels.map((currTab) => {
    const filteredFeatureTab = filteredTabs.find(
      (currFeatureTab) =>
        currFeatureTab.name === currTab.label &&
        currFeatureTab.viewaccess === true,
    )
    if (filteredFeatureTab !== undefined) {
      return {
        ...currTab,
        ...filteredFeatureTab,
      }
    }
    return {
      ...currTab,
      viewaccess: false,
      createaccess: false,
      updateaccess: false,
      deleteaccess: false,
    }
  })
}

export const roundUp = (value: number): number => {
  const baseNum = Math.trunc(value)
  if (baseNum < value && baseNum + 1 > value) {
    return baseNum + 1
  }
  return value
}

// Utilized in New Event Child Components for formatting time
export const convertTime = (timeString: string): string => {
  const [time, modifier] = timeString.split(' ')
  /*eslint prefer-const: ["error", {"destructuring": "all"}]*/
  let [hours, minutes] = time.split(':')
  if (hours === '12') {
    hours = '00'
  }
  if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12)
  }
  return `${hours}:${minutes}`
}
// eslint-disable-next-line
export const removeHtmlTags = (inputString: string) => {
  return inputString?.replace(/<[^>]*>/g, '')
}

export const convertUserName = (userName: string): string => {
  let result = userName
  const parts = userName.toLowerCase().split(/\s+/)

  if (parts.length === 1) {
    return result
  } else if (parts.length === 2) {
    result = parts[0] + '.' + parts[1]
  } else if (parts.length >= 3) {
    result = parts[0] + parts[1] + '.' + parts[2]
  }

  if (result.includes('undefined')) {
    return userName
  } else return result
}

export const limitChar = (jobVacancy: GetAllJobVacanciesList): string => {
  //This separation of conditions is intentional as it is not working
  //if both conditions are in one if block statement
  if (jobVacancy.description === null) {
    return 'N/A'
  }
  if (jobVacancy?.description !== '') {
    const removeTag = `${jobVacancy?.description
      ?.replace(/<[^>]+>/g, '')
      ?.replace(/:/g, '')}`
    return removeTag && removeTag?.length > 30
      ? `${removeTag?.substring(0, 30)}...`
      : removeTag
  } else {
    return 'N/A'
  }
}
// TODO: Relocate this function to a more appropriate or related file (e.g. dateUtils)
export const formatDateMinusOne = (date: Date): string => {
  const year = date.getFullYear()
  const month = date.getMonth()
  if (month === 0) {
    return `0/${year}`
  } else {
    const newDate = new Date(date)
    newDate.setMonth(newDate.getMonth() - 1)
    const formattedMonth = newDate.getMonth() + 1
    const formattedYear = newDate.getFullYear()
    return `${formattedMonth}/${formattedYear}`
  }
}
// TODO: Relocate this function to a more appropriate or related file (e.g. dateUtils)
export const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0')
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const month = monthNames[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

/**
 * Checks if the given value is an object.
 *
 * @param obj - The value to check.
 * @returns `true` if the value is an object, `false` otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isObject(obj: any): obj is Record<string, any> {
  return obj !== null && typeof obj === 'object'
}

/**
 * Performs a deep comparison between two objects to determine if they are equal.
 * @template T - The type of the objects being compared.
 * @param obj1 - The first object to compare.
 * @param obj2 - The second object to compare.
 * @returns A boolean indicating whether the two objects are equal.
 * @note This function will compare nested objects within the object.
 */
export function deepEqualComparison<T>(obj1: T, obj2: T): boolean {
  if (Object.is(obj1, obj2)) {
    return true
  }

  if (!isObject(obj1) || !isObject(obj2)) {
    return false
  }

  const keys1 = Object.keys(obj1) as (keyof T)[]
  const keys2 = Object.keys(obj2) as (keyof T)[]

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    const val1 = obj1[key]
    const val2 = obj2[key]

    const areObjects = isObject(val1) && isObject(val2)

    if (
      (areObjects && !deepEqualComparison(val1, val2)) ||
      (!areObjects && !Object.is(val1, val2))
    ) {
      return false
    }
  }

  return true
}

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const dynamicFormLabelProps = (
  htmlFor: string,
  className: string,
): Label => {
  return {
    htmlFor,
    className,
  }
}

export function javaNewLineToHtmlBreak(text: string) {
  return text
    ? String(text).replace(/(?:\r\n|\r|\n)(?:\s*(?:\r\n|\r|\n))+/g, '<br />')
    : ''
}

export function cleanHtmlContent(htmlContent: string) {
  return (
    htmlContent
      ?.replace(/<p>((.|\n|\r)*?)<\/p>/, '$1')
      ?.replace(/<\/p>(?![\s\S]*<\/p>)/, '') // Remove the last </p>
      // .replace(/<p>(?![\s\S]*<p>)/, '') // Remove the last <p>
      ?.replace(/\.(?!.*\.)/, '') // Remove the last .
  )
}

/**
 * Retrieves the value of a property from an object.
 *
 * @param obj - The object from which to retrieve the property value.
 * @param key - The key of the property to retrieve.
 * @returns The value of the specified property.
 *
 * @typeParam T - The type of the object.
 * @typeParam K - The type of the property key.
 *
 * @example
 * // This function will return the value of the property of the specified object type //
 * // Be sure that the object has the type of T and T has the property K //
 * getProperty(object, propertyName as keyof ObjectType)
 */
export function getProperty<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key]
}

/**
 * Returns the result based on a condition with dynamic typing.
 *
 * @template T - The type of the true result.
 * @template U - The type of the false result.
 * @param {boolean} condition - The condition to evaluate.
 * @param {T} trueResult - The result when the condition is true.
 * @param {U} falseResult - The result when the condition is false.
 * @returns {T | U} - The true result if the condition is true, otherwise the false result.
 */
export function conditionalResult<T, U>(
  condition: boolean,
  trueResult: T,
  falseResult: U,
): T | U {
  return condition ? trueResult : falseResult
}

/**
 * Checks if uploaded file has accepted extensions.
 *
 * @param {File[]} files - The array of files to check.
 * @param {string[]} acceptedExtensions - The array of accepted extensions.
 * @returns {boolean} - True if all files have accepted extensions, otherwise false.
 */
export function areAllFilesAccepted(
  files: File[],
  acceptedExtensions: string[],
): boolean {
  return files.every((file) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    return acceptedExtensions.includes(fileExtension || '')
  })
}

// TODO: Relocate this function to a more appropriate or related file (e.g. dateUtils)
/**
 * Gets the current year based on the local time zone
 */
export const currentYear = new Date().getFullYear()

// TODO: Relocate this function to a more appropriate or related file (e.g. dateUtils)
/**
 * Gets the current date in ISO format
 */
export const today = new Date().toISOString()

/**
 * Displays the index based on the pagination values
 * @param index - The index of the item
 * @param currentPage - The current page number
 * @param pageSize - The number of items per page
 * @returns The item number
 */
// TODO: Relocate this function to a more appropriate or related file (e.g. pagination)
export const getItemNumber = (
  index: number,
  currentPage: number,
  pageSize: number,
): number => {
  return (currentPage - 1) * pageSize + index + 1
}

/**
 * Converts local time to IST
 */
export const todayToIST = (() => {
  const date = new Date()

  const utcOffset = date.getTimezoneOffset()
  const istOffset = 330
  const istDate = new Date(date.getTime() + (istOffset + utcOffset) * 60000)

  const day = String(istDate.getDate()).padStart(2, '0')
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const month = monthNames[istDate.getMonth()]
  const year = istDate.getFullYear()
  const hours = String(istDate.getHours()).padStart(2, '0')
  const minutes = String(istDate.getMinutes()).padStart(2, '0')
  const seconds = String(istDate.getSeconds()).padStart(2, '0')

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}:IST`
})()

export const checkBooleanStorage = (key: string): boolean => {
  const value = sessionStorage.getItem(key)
  return value === 'true' && value !== null
}

export const createNumberArray = (maxLength: number): number[] => {
  return Array.from({ length: maxLength }, (_, index) => index + 1)
}

/**
 * Calculates the size and page number based on the given start and end indices.
 *
 * @param startIndex - The starting index.
 * @param endIndex - The ending index.
 * @returns An object containing the size and page number.
 */
export function calculateSizeAndPage(
  startIndex: number,
  endIndex: number,
): { size: number; page: number } {
  let size = endIndex - startIndex

  // Ensure the size is not below 20 if startIndex is 1
  if (startIndex === 1 && size < 20) {
    size = 20
  }

  const page = Math.floor(Math.abs(startIndex / size + 1))
  return { size, page }
}

export function getDateRangeFromTodayToNextYear(): {
  minDate: Date
  maxDate: Date
} {
  const today = new Date()
  const currentYear = today.getFullYear()

  const minDate = today // disables past dates
  const maxDate = new Date(currentYear + 1, 11, 31) // Dec 31 of next year

  return { minDate, maxDate }
}

export function getDateRangeFromCurrentYearToNextYear(): {
  minDate: Date
  maxDate: Date
} {
  const today = new Date()
  const currentYear = today.getFullYear()

  const minDate = new Date(currentYear, 0, 1) // Jan 1st of the current year
  const maxDate = new Date(currentYear + 1, 11, 31) // Dec 31st of next year

  return { minDate, maxDate }
}
export function getDatePickerRangeFromTodayToNextYear(): {
  minDate: Date
  maxDate: Date
} {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const maxYear = today.getFullYear() + 1
  const maxDate = new Date(maxYear, 11, 31)

  return { minDate: today, maxDate }
}
export function getDateRangeForNextFiveYears(): {
  minDate: Date
  maxDate: Date
} {
  const today = new Date()
  const currentYear = today.getFullYear()

  const minDate = new Date(currentYear, 0, 1) // Jan 1 of current year
  const maxDate = new Date(currentYear + 5, 11, 31) // Dec 31 of year + 5

  return { minDate, maxDate }
}

export function getDatePickerRange2000ToNextYear(): {
  minDate: Date
  maxDate: Date
} {
  const minDate = new Date(2000, 0, 1) // Jan 1, 2000
  const maxYear = new Date().getFullYear() + 1
  const maxDate = new Date(maxYear, 11, 31) // Dec 31 of next year

  return { minDate, maxDate }
}

export function getMinMaxDatesForPassportDetails(): {
  minDate: Date
  maxDate: Date
} {
  const minDate = new Date(2000, 0, 1) // Jan 1, 2000
  const maxYear = new Date().getFullYear() + 10
  const maxDate = new Date(maxYear, 11, 31) // Dec 31 of next year

  return { minDate, maxDate }
}

export const formatCountryName = (name: string): string => {
  const uppercaseCountries = ['USA', 'UK', 'US', 'UAE', 'EU', 'UN', 'NATO']
  const cleanName = name?.trim()
  if (!cleanName) return ''
  return uppercaseCountries.includes(cleanName.toUpperCase())
    ? cleanName.toUpperCase()
    : cleanName.charAt(0).toUpperCase() + cleanName.slice(1).toLowerCase()
}
